import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Layout from '../Layout';
import ResponsiveTable from '../ResponsiveTable';
import { useAuth } from '../../context/AuthContext';
import { hasAnyPermission } from '../../utils/permissions';
import { formatCurrency } from '../../utils/currency';
import { formatDateTime } from '../../utils/dateFormat';
import { usePageNotice } from '../../context/FeedbackContext';

const DASHBOARD_METRIC_PERMISSIONS = [
  'view_dashboard_today',
  'view_dashboard_monthly_payments',
  'view_dashboard_monthly_orders',
  'view_dashboard_monthly_cancellations',
  'view_dashboard_open_balance',
];

const DashboardMetricListPage = ({
  apiPath,
  title,
  subtitle,
  emptyMessage,
  loadFailedMessage,
  countLabel,
  columns: extraColumns,
  renderMobileCardExtra,
  getRowNavigatePath,
  requiredPermissions = DASHBOARD_METRIC_PERMISSIONS,
}) => {
  const { t } = useTranslation();
  const { userRole, customPermissions } = useAuth();
  const canAccess = hasAnyPermission(userRole, requiredPermissions, customPermissions);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState({
    metricsCurrency: 'USD',
    count: 0,
    total: 0,
    rows: [],
    period: 'today',
  });
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(apiPath);
      setPayload(res.data || { rows: [], total: 0, count: 0 });
    } catch (err) {
      setError(
        err.userMessage
        || err.response?.data?.error
        || loadFailedMessage
        || t('dashboard.metricsDetail.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [apiPath, loadFailedMessage, t]);

  useEffect(() => {
    if (canAccess) fetchData();
  }, [canAccess, fetchData, apiPath]);

  usePageNotice(
    `dashboard-metric-${apiPath}`,
    error ? { message: error, severity: 'error', persist: true } : null,
  );

  const currency = payload.metricsCurrency || 'USD';

  const sortedRows = useMemo(() => {
    const list = [...(payload.rows || [])];
    const dir = sortOrder === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortBy === 'amount') {
        return (a.amount - b.amount) * dir;
      }
      if (sortBy === 'client') {
        const aName = (a.clientUser?.name || '').toLowerCase();
        const bName = (b.clientUser?.name || '').toLowerCase();
        if (aName < bName) return -1 * dir;
        if (aName > bName) return 1 * dir;
        return 0;
      }
      const aTime = new Date(a.date).getTime();
      const bTime = new Date(b.date).getTime();
      return (aTime - bTime) * dir;
    });
    return list;
  }, [payload.rows, sortBy, sortOrder]);

  if (!canAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const formatMoney = (n) => formatCurrency(n, currency);

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(columnId);
      setSortOrder(columnId === 'client' ? 'asc' : 'desc');
    }
  };

  const baseColumns = [
    {
      id: 'client',
      label: t('dashboard.metricsDetail.client'),
      sortable: true,
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {row.clientUser?.name || '—'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'description',
      label: t('dashboard.activity.description'),
      render: (row) => (
        <Typography variant="body2">{row.description || '—'}</Typography>
      ),
    },
    {
      id: 'amount',
      label: t('dashboard.activity.amount'),
      sortable: true,
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {formatMoney(row.amount)}
        </Typography>
      ),
    },
    {
      id: 'date',
      label: t('common.date'),
      sortable: true,
      render: (row) => (
        <Typography variant="body2">{formatDateTime(row.date)}</Typography>
      ),
    },
  ];

  const columns = extraColumns || baseColumns;

  const defaultMobileCard = (row) => (
    <Card
      variant="outlined"
      sx={{ cursor: getRowNavigatePath ? 'pointer' : 'default' }}
      onClick={() => getRowNavigatePath?.(row)}
    >
      <CardContent>
        <Typography variant="subtitle2" fontWeight={600}>
          {row.clientUser?.name || '—'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {row.description}
        </Typography>
        <Typography variant="body1" fontWeight={700}>
          {formatMoney(row.amount)}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {formatDateTime(row.date)}
        </Typography>
        {renderMobileCardExtra?.(row)}
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Button
          component={RouterLink}
          to="/dashboard"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
          size="small"
        >
          {t('dashboard.metricsDetail.backToDashboard')}
        </Button>

        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography color="text.secondary" variant="body2" gutterBottom>
                  {countLabel}
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {formatMoney(payload.total)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('dashboard.activity.itemCount', { count: payload.count || 0 })}
                </Typography>
              </CardContent>
            </Card>

            {sortedRows.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {emptyMessage}
              </Typography>
            ) : (
              <ResponsiveTable
                columns={columns}
                data={sortedRows}
                renderMobileCard={defaultMobileCard}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
                onRowClick={getRowNavigatePath}
                getStatusColor={() => 'default'}
              />
            )}
          </>
        )}
      </Box>
    </Layout>
  );
};

export default DashboardMetricListPage;
